import * as React from 'react';
import { useState } from 'react';

import { sendTeacherAmplitudeEvent } from '~analytics/amplitude';
import { EventTypes } from '~analytics/types';
import { translate } from '~l10n/client';
import { IconButton } from '~shared/components/buttons/IconButton';
import { IconThumbsDown } from '~shared/components/icons/standard/IconThumbsDown/IconThumbsDown';
import { IconThumbsUp } from '~shared/components/icons/standard/IconThumbsUp/IconThumbsUp';

import {
  RateQuestionQualityAiDetails,
  RateQuestionQualityFeedbackDialog,
  RateQuestionQualitySource,
} from './RateQuestionQualityFeedbackDialog';

type Rating = 'helpful' | 'not-helpful' | null;

interface RateQuestionQualityProps {
  source: RateQuestionQualitySource;
  aiDetails?: RateQuestionQualityAiDetails;
}

export function RateQuestionQuality({
  source,
  aiDetails,
}: RateQuestionQualityProps) {
  const [selectedRating, setSelectedRating] = useState<Rating>(null);
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  const labelHelpful = translate('Helpful');
  const labelNotHelpful = translate('Not helpful');

  const toggleRating = (rating: Exclude<Rating, null>) => {
    if (rating === 'not-helpful') {
      if (selectedRating === 'not-helpful') {
        setSelectedRating(null);
      } else {
        setIsFeedbackDialogOpen(true);
        setSelectedRating(rating);
      }
    } else {
      if (selectedRating === 'helpful') {
        setSelectedRating(null);
      } else {
        sendTeacherAmplitudeEvent(EventTypes.RateQuestionQuality, {
          question_quality_rating: 'helpful',
          source,
          details: {
            insight_id: aiDetails?.insightId ?? null,
            ai_prompt_version: aiDetails?.promptVersion,
          },
        });
        setSelectedRating(rating);
      }
    }
  };

  const isHelpfulSelected = selectedRating === 'helpful';
  const isNotHelpfulSelected = selectedRating === 'not-helpful';

  const showThumbsUp = selectedRating === null || isHelpfulSelected;
  const showThumbsDown = selectedRating === null || isNotHelpfulSelected;

  return (
    <div className="flex items-center justify-end gap-1 pr-4">
      <span className="text-xs text-blue-600 font-400">
        {translate('Question Quality')}
      </span>

      <div className="flex items-center">
        {showThumbsUp && (
          <IconButton
            onClick={() => toggleRating('helpful')}
            aria-label={labelHelpful}
            aria-pressed={isHelpfulSelected}
            appearance="default"
          >
            <IconThumbsUp
              title={labelHelpful}
              titleId="rate-question-quality-helpful"
              className={isHelpfulSelected ? 'fill-current' : ''}
            />
          </IconButton>
        )}

        {showThumbsDown && (
          <IconButton
            onClick={() => toggleRating('not-helpful')}
            aria-label={labelNotHelpful}
            aria-pressed={isNotHelpfulSelected}
            appearance="default"
          >
            <IconThumbsDown
              title={labelNotHelpful}
              titleId="rate-question-quality-not-helpful"
              className={isNotHelpfulSelected ? 'fill-current' : ''}
            />
          </IconButton>
        )}
      </div>

      <RateQuestionQualityFeedbackDialog
        open={isFeedbackDialogOpen}
        onClose={() => setIsFeedbackDialogOpen(false)}
        source={source}
        aiDetails={aiDetails}
      />
    </div>
  );
}
